/**
 * Helpers for the FinFactor (account aggregator) consent journey.
 *
 * The AA data does not land on our side the moment the consent turns
 * APPROVED — the FIPs keep pushing it for a few seconds after that. So when
 * the user comes back to the Financial Overview page right after finishing the
 * journey, we refresh every FinFactor API a few times instead of once.
 *
 * A sessionStorage flag is used (not router state) because the web journey
 * leaves the SPA entirely — `window.location.href = redirectUrl` — and comes
 * back through a full page load, which drops any in-memory/router state.
 */

const JOURNEY_PENDING_KEY = "finfactorJourneyPending";

/** Number of times each API is called after the journey completes. */
export const FINFACTOR_REFRESH_ROUNDS = 3;

/** Gap between those calls. */
export const FINFACTOR_REFRESH_INTERVAL_MS = 5000;

/**
 * Tags covering every FinFactor-backed query. Invalidating them refetches all
 * of the mounted FinFactor APIs (dashboard, consents, portfolio, banking,
 * insurance) in one go.
 */
export const FINFACTOR_REFRESH_TAGS = [
    "Dashboard",
    "Consents",
    "AccountAggregator",
    "InvestmentPortfolio",
    "BankingAccounts",
    "InsurancePolicies",
    "Portfolio",
];

/** Call right before handing the user off to the consent journey. */
export const markFinFactorJourneyPending = () => {
    try {
        sessionStorage.setItem(JOURNEY_PENDING_KEY, "true");
    } catch (error) {
        console.warn("Failed to mark FinFactor journey as pending:", error);
    }
};

/** Reads and clears the flag, so the refresh burst runs only once per journey. */
export const consumeFinFactorJourneyPending = () => {
    try {
        if (sessionStorage.getItem(JOURNEY_PENDING_KEY) === "true") {
            sessionStorage.removeItem(JOURNEY_PENDING_KEY);
            return true;
        }
    } catch (error) {
        console.warn("Failed to read FinFactor journey flag:", error);
    }
    return false;
};

export const clearFinFactorJourneyPending = () => {
    try {
        sessionStorage.removeItem(JOURNEY_PENDING_KEY);
    } catch (error) {
        console.warn("Failed to clear FinFactor journey flag:", error);
    }
};
